// ABOUTME: Relation-facing modal that creates a return without an order, including its shipping label.
// ABOUTME: Picks a destination country + return shipper profile, collects the sender address and the expected products.
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import * as yup from "yup";

import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ShPayloadToggle from "../../theme/ShPayloadToggle";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import ShValidatedInput from "../../theme/inputs/ShValidatedInput";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import NumberInput from "../../theme/inputs/NumberInput";
import ShSwitch from "../../theme/inputs/ShSwitch";
import MSelectLabel from "./MSelectLabel";
import { useCreateReturnLabelMutation } from "../../../newapi/return/returnsSlice";
import { useGetRelationAddressSelectQuery } from "../../../newapi/relationaddress/relationaddressSlice";
import useDutchStreetLookup from "../../../hooks/useDutchStreetLookup";
import { useGetProductsSelectQuery } from "../../../newapi/warehouse/productSlice";
import { useGetReturnReasonsSelectQuery } from "../../../newapi/inbound/returnsSlice";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import { yupRequired } from "../../../utils/validation";
import { countryCodeNL } from '../../../utils/constants';
import { countriesFromLocale } from '../../../utils/countries';

// Sentinel id for the "Geen" (none) option that clears the address fields.
const NO_ADDRESS_OPTION_ID = 'none';

const emptyAddress = {
    name: "",
    companyName: "",
    street: "",
    houseNumber: "",
    houseNumberAddition: "",
    postalCode: "",
    city: "",
    country: "",
    email: "",
    phone: "",
};

const initialForm = () => ({
    createLabel: true,
    rejectAll: false,
    country: countryCodeNL,
    shipperProfileId: null,
    name: "",
    reference: "",
    sender: { ...emptyAddress, country: countryCodeNL },
    returnLines: [],
});

// A return profile is available for a country when its returnOptions.countries lists that
// country. Unlike an outbound profile an empty list means unavailable, mirroring how the
// return portal offers its shippers. Comparison is case-insensitive.
const isProfileAvailableForCountry = (profile, country) => {
    const list = profile.returnOptions?.countries;
    if (!list || list.length === 0) return false;
    return list.map(c => c.toUpperCase()).includes((country || "").toUpperCase());
};

const MCreateReturn = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const profiles = useMemo(() => props.profiles ?? [], [props.profiles]);

    const [createReturnLabel, { isLoading }] = useCreateReturnLabelMutation();

    const [form, setForm] = useState(initialForm());
    const [fieldErrors, setFieldErrors] = useState({});
    const [result, setResult] = useState(null);
    // What the carrier itself said when it refused the label, so the reason is readable
    // instead of hidden behind the generic failure message.
    const [shippingErrors, setShippingErrors] = useState([]);
    const [shippingPayload, setShippingPayload] = useState(null);
    const [labelsModal, setLabelsModal] = useState({ open: false, labels: [], type: null });

    const language = useSelector((state) => state.settings.language);
    const countries = countriesFromLocale(language);

    const relationId = useSelector((state) => state.settings.environment.selectedId);

    // Fetch the relation's saved (standard) addresses. The backend scopes the
    // result to the active relation via the relation_id request header.
    const { data: relationAddresses } = useGetRelationAddressSelectQuery();

    // For a Dutch sender address, postal code + house number fill in the street and city.
    const streetLookup = useDutchStreetLookup(form.sender, ({ street, city }) => {
        setForm(prev => ({ ...prev, sender: { ...prev.sender, street, city: city || prev.sender.city } }));
        setFieldErrors(prev => ({ ...prev, "sender.street": '', "sender.city": '' }));
    });

    // The products the relation can announce as coming back.
    const { data: products } = useGetProductsSelectQuery(
        { relationId: relationId, ignoreVirtual: false },
        { skip: !relationId }
    );

    const productOptions = useMemo(() => (products ?? []).map(product => ({
        id: product.id,
        name: product.combined,
        barcodes: product.barcodes,
    })), [products]);

    // The reasons the relation has configured. A relation with none simply gets no selector.
    const { data: reasons } = useGetReturnReasonsSelectQuery(
        { relationId: relationId },
        { skip: !relationId }
    );

    const reasonOptions = useMemo(() => (reasons ?? []).map(reason => ({
        id: reason.id,
        name: reason.description,
    })), [reasons]);

    // Index of the currently selected saved address in the dropdown.
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);

    // Reset the form and any prior result every time the modal is opened, so a return
    // created a moment ago never leaves its result or its lines behind for the next one.
    useEffect(() => {
        if (props.open) {
            setForm(initialForm());
            setFieldErrors({});
            setResult(null);
            setShippingErrors([]);
            setShippingPayload(null);
            setSelectedAddressIndex(-1);
            setLabelsModal({ open: false, labels: [], type: null });
        }
    }, [props.open]);

    // Prepend a "Geen" option that clears the address fields when selected.
    const relationAddressOptions = useMemo(() => {
        if (!relationAddresses || relationAddresses.length === 0) return [];
        return [
            { id: NO_ADDRESS_OPTION_ID, name: t("none") },
            ...relationAddresses
        ];
    }, [relationAddresses, t]);

    // Fill the sender fields from a selected saved address, or clear them when the "Geen"
    // option is picked. The sender country stays tied to the top-level country selector
    // that drives shipper availability.
    const handleRelationAddressChange = (key, val) => {
        const index = relationAddressOptions.findIndex(o => o.id === val);
        setSelectedAddressIndex(index);

        if (val === NO_ADDRESS_OPTION_ID) {
            setForm(prev => ({
                ...prev,
                sender: { ...emptyAddress, country: prev.sender.country },
            }));
            setFieldErrors({});
            return;
        }

        const address = relationAddressOptions[index]?.addressObject;
        if (!address) return;

        setForm(prev => ({
            ...prev,
            sender: {
                ...prev.sender,
                name: [address.firstName, address.lastName].filter(Boolean).join(" "),
                companyName: address.company ?? "",
                street: address.street ?? "",
                houseNumber: address.houseNumber ?? "",
                houseNumberAddition: address.houseNumberAddition ?? "",
                postalCode: address.postalCode ?? "",
                city: address.city ?? "",
                email: address.email ?? "",
                phone: address.phone ?? "",
            },
        }));
        setFieldErrors({});
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleClose = () => {
        setForm(initialForm());
        setFieldErrors({});
        setResult(null);
        setShippingErrors([]);
        setShippingPayload(null);
        setSelectedAddressIndex(-1);
        setLabelsModal({ open: false, labels: [], type: null });
        props.handleClose();
    };

    // Profiles valid for the currently selected country.
    const availableProfiles = useMemo(
        () => profiles.filter(p => isProfileAvailableForCountry(p, form.country)),
        [profiles, form.country]
    );

    // Options carry a composite display label (name — carrier).
    const profileOptions = useMemo(() => availableProfiles.map(p => ({
        ...p,
        label: `${p.name} — ${p.shipper?.name ?? ""}`,
    })), [availableProfiles]);

    const selectedProfile = useMemo(
        () => profiles.find(p => p.id === form.shipperProfileId) ?? null,
        [profiles, form.shipperProfileId]
    );

    const hasAvailableProfiles = availableProfiles.length > 0;

    // The rest of the form only makes sense once a label has a shipper to go with it. Without
    // a label there is nothing to wait for.
    const showDetails = !form.createLabel || (hasAvailableProfiles && selectedProfile);

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setFieldErrors(prev => ({ ...prev, [key]: '' }));
    };

    // Turning the label off drops the shipper choice; the country and the address stay as
    // they were, so turning it back on does not throw away what was already filled in.
    const handleCreateLabelChange = (checked) => {
        setForm(prev => ({ ...prev, createLabel: checked, shipperProfileId: checked ? prev.shipperProfileId : null }));
        setFieldErrors({});
    };

    const handleCountryChange = (key, value) => {
        setForm(prev => {
            // If the selected profile is no longer valid for the new country, clear it.
            const stillValid = prev.shipperProfileId != null &&
                profiles.filter(p => isProfileAvailableForCountry(p, value))
                    .some(p => p.id === prev.shipperProfileId);
            return {
                ...prev,
                country: value,
                shipperProfileId: stillValid ? prev.shipperProfileId : null,
                sender: { ...prev.sender, country: value },
            };
        });
    };

    const handleSenderChange = (key, value) => {
        setForm(prev => ({ ...prev, sender: { ...prev.sender, [key]: value } }));
        setFieldErrors(prev => ({ ...prev, [`sender.${key}`]: '' }));
    };

    const handleProfileChange = (key, value) => {
        setForm(prev => ({ ...prev, shipperProfileId: value }));
        setFieldErrors(prev => ({ ...prev, shipperProfileId: '' }));
    };

    const addReturnLine = () => {
        setForm(prev => ({ ...prev, returnLines: [...prev.returnLines, { productId: null, amount: 1, returnReasonId: null }] }));
        setFieldErrors(prev => ({ ...prev, returnLines: '' }));
    };

    const handleLineReasonChange = (key, value) => {
        const index = parseInt(key.split(".")[0]);
        setForm(prev => {
            const lines = [...prev.returnLines];
            lines[index] = { ...lines[index], returnReasonId: value };
            return { ...prev, returnLines: lines };
        });
    };

    const handleLineProductChange = (key, value) => {
        const index = parseInt(key.split(".")[0]);
        setForm(prev => {
            const lines = [...prev.returnLines];
            lines[index] = { ...lines[index], productId: value };
            return { ...prev, returnLines: lines };
        });
        setFieldErrors(prev => ({ ...prev, returnLines: '' }));
    };

    const handleLineAmountChange = (index, value) => {
        setForm(prev => {
            const lines = [...prev.returnLines];
            lines[index] = { ...lines[index], amount: value };
            return { ...prev, returnLines: lines };
        });
        setFieldErrors(prev => ({ ...prev, returnLines: '' }));
    };

    const handleDeleteLine = (index) => {
        setForm(prev => ({ ...prev, returnLines: prev.returnLines.filter((_, i) => i !== index) }));
    };

    const addressSchema = yup.object().shape({
        name: yupRequired('name', t),
        street: yupRequired('street', t),
        houseNumber: yupRequired('house_nr', t),
        postalCode: yupRequired('postal', t),
        city: yupRequired('city', t),
        country: yupRequired('country', t),
    });

    const runSchema = async (schema, data, prefix) => {
        try {
            await schema.validate(data, { abortEarly: false });
            return {};
        } catch (err) {
            if (err.name === 'ValidationError') {
                return err.inner.reduce((acc, e) => ({ ...acc, [`${prefix}.${e.path}`]: e.message }), {});
            }
            throw err;
        }
    };

    // The relation announces the product and how many of it; accepting or rejecting each
    // line is the warehouse's call once the parcel arrives.
    const validLines = useMemo(
        () => form.returnLines.filter(line => line.productId > 0 && parseInt(line.amount) > 0),
        [form.returnLines]
    );

    const openSingleLabel = (content, type) => {
        let url = "";
        if (type === 'pdf') {
            const decodedData = atob(content);
            const arrayBuffer = new ArrayBuffer(decodedData.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < decodedData.length; i++) {
                uint8Array[i] = decodedData.charCodeAt(i);
            }
            const blob = new Blob([uint8Array], { type: 'application/pdf' });
            url = URL.createObjectURL(blob);
        } else if (type === 'link') {
            url = content;
        }
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
            setTimeout(() => { window.URL.revokeObjectURL(url); }, 500);
        } else {
            window.URL.revokeObjectURL(url);
        }
    };

    const handleSubmit = async () => {
        if (isLoading) return;
        if (form.createLabel && !selectedProfile) return;

        setFieldErrors({});
        setShippingErrors([]);
        setShippingPayload(null);

        // Only a label needs an address to send from and contents to weigh. An announced
        // return is identified by its reference alone.
        const allErrors = form.createLabel
            ? await runSchema(addressSchema, form.sender, 'sender')
            : {};

        if (!form.name) {
            allErrors.name = t("required");
        }
        if (form.createLabel && validLines.length === 0) {
            allErrors.returnLines = t("return_lines_required");
        }
        if (!form.createLabel && !form.reference) {
            allErrors.reference = t("required");
        }

        if (Object.keys(allErrors).length > 0) {
            setFieldErrors(allErrors);
            return;
        }

        const payload = {
            createLabel: form.createLabel,
            name: form.name,
            reference: form.reference,
            rejectAll: form.rejectAll,
            returnLines: validLines.map(line => ({
                productId: line.productId,
                amount: parseInt(line.amount),
                ...(line.returnReasonId > 0 ? { returnReasonId: line.returnReasonId } : {}),
            })),
            ...(form.createLabel ? {
                shipperProfileId: selectedProfile.id,
                sender: {
                    ...form.sender,
                    country: (form.sender.country || "").toUpperCase(),
                },
            } : {}),
        };

        try {
            const data = await createReturnLabel(payload).unwrap();

            const labels = data.labels ?? [];
            if (labels.length > 1) {
                setLabelsModal({ open: true, labels, type: data.labelType });
            } else if (labels.length === 1) {
                openSingleLabel(labels[0], data.labelType);
            }

            setResult({ barcode: data.barcode, reference: data.reference });

            showToastMessagePostRequest(t, data);
            props.onCreated?.(data);
        } catch (err) {
            // The carriers say through `errors` what they refused and why; the toast only
            // carries the headline, so the messages themselves are kept on screen.
            // A few carriers hand back a structured error rather than a line of text, so
            // everything is rendered as text and nothing can break the modal.
            setShippingErrors((err?.data?.errors ?? []).map(e => typeof e === "string" ? e : JSON.stringify(e)));
            setShippingPayload(err?.data?.payload ?? null);
            showToastMessageRequestError(t("create_failed"), t, err);
        }
    };

    const rowSx = {
        width: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 7,
    };
    const inputBoxSx = {
        flex: 1,
        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
        borderRadius: 4,
        display: "flex",
        height: 32,
    };

    const TextField = ({ label, name, value, error, isSender, width = 120, placeholder, loading }) => (
        <Box sx={rowSx}>
            <Box sx={{ width: width }}>
                <Text>{label}</Text>
            </Box>
            <Box sx={inputBoxSx}>
                <ShValidatedInput
                    name={name}
                    value={value}
                    placeholder={placeholder}
                    loading={loading}
                    onChange={isSender ? handleSenderChange : handleChange}
                    error={error}
                />
            </Box>
        </Box>
    );

    const renderSenderFields = (data) => (
        <>
            {TextField({ label: t("name"), name: "name", value: data.name, error: fieldErrors["sender.name"], isSender: true })}
            {TextField({ label: t("company_name"), name: "companyName", value: data.companyName, error: fieldErrors["sender.companyName"], isSender: true })}
            {TextField({ label: t("postal"), name: "postalCode", value: data.postalCode, error: fieldErrors["sender.postalCode"], isSender: true })}
            {TextField({ label: t("house_nr"), name: "houseNumber", value: data.houseNumber, error: fieldErrors["sender.houseNumber"], isSender: true })}
            {TextField({ label: t("house_nr_extra"), name: "houseNumberAddition", value: data.houseNumberAddition, error: fieldErrors["sender.houseNumberAddition"], isSender: true })}
            {TextField({ label: t("street"), name: "street", value: data.street, error: fieldErrors["sender.street"], isSender: true, loading: streetLookup.isLoading, placeholder: streetLookup.isLoading ? t("address_lookup_searching") : undefined })}
            {streetLookup.notFound &&
                <Box sx={rowSx}>
                    <Box sx={{ width: 120 }} />
                    <Box sx={{ flex: 1, pl: 2 }}>
                        <Text light>{t("address_lookup_not_found")}</Text>
                    </Box>
                </Box>
            }
            {TextField({ label: t("city"), name: "city", value: data.city, error: fieldErrors["sender.city"], isSender: true, loading: streetLookup.isLoading, placeholder: streetLookup.isLoading ? t("address_lookup_searching") : undefined })}
            {TextField({ label: t("email"), name: "email", value: data.email, error: fieldErrors["sender.email"], isSender: true })}
            {TextField({ label: t("phone"), name: "phone", value: data.phone, error: fieldErrors["sender.phone"], isSender: true })}
        </>
    );

    const submitDisabled = isLoading || (form.createLabel && (!selectedProfile || !hasAvailableProfiles));

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1
                }}>
                <Subheading>{t("create_return_without_order")}</Subheading>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    maxHeight: "60vh",
                    overflowY: "auto",
                }}
            >
                {result ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Text bold>{t("return_created")}</Text>
                        {result.reference &&
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Text light>{t("reference")}</Text>
                                <Text bold>{result.reference}</Text>
                            </Box>
                        }
                        {result.barcode &&
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Text light>{t("tracking_number")}</Text>
                                <Text bold>{result.barcode}</Text>
                            </Box>
                        }
                    </Box>
                ) : (
                    <>
                        {/* A label is optional. Without one the return is only announced and
                            a name and a reference are all it takes. */}
                        <Box sx={rowSx}>
                            <Box sx={{ width: 160 }}>
                                <Text>{t("create_shipping_label")}</Text>
                            </Box>
                            <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                                <ShSwitch
                                    checked={form.createLabel}
                                    onChange={(e) => handleCreateLabelChange(e.target.checked)}
                                />
                            </Box>
                        </Box>

                        {form.createLabel &&
                        <>
                        {/* Country drives which return profiles are valid */}
                        <Box sx={rowSx}>
                            <Box sx={{ width: 160 }}>
                                <Text>{t("country")}</Text>
                            </Box>
                            <Box sx={inputBoxSx}>
                                <ShValidatedInputDropdown
                                    name="country"
                                    displayName="name"
                                    changeField="alpha2"
                                    options={countries ?? null}
                                    noSelection={t("choose_option")}
                                    onChange={handleCountryChange}
                                    selected={countries?.findIndex(c => c.alpha2 === form.country) ?? -1}
                                    error={fieldErrors.country}
                                    searchOptions={{
                                        enabled: true,
                                        placeholder: t("search")
                                    }}
                                />
                            </Box>
                        </Box>

                        {!hasAvailableProfiles ? (
                            <Box sx={{ paddingY: 2 }}>
                                <Text light>
                                    {t("no_return_option_for_country", { country: (form.country || "").toUpperCase() })}
                                </Text>
                            </Box>
                        ) : (
                            <>
                                {/* Profile selector */}
                                <Box sx={rowSx}>
                                    <Box sx={{ width: 160 }}>
                                        <Text>{t("shipper")}</Text>
                                    </Box>
                                    <Box sx={inputBoxSx}>
                                        <ShValidatedInputDropdown
                                            name="shipperProfileId"
                                            displayName="label"
                                            changeField="id"
                                            options={profileOptions}
                                            noSelection={t("choose_option")}
                                            onChange={handleProfileChange}
                                            selected={profileOptions.findIndex(p => p.id === form.shipperProfileId)}
                                            error={fieldErrors.shipperProfileId}
                                        />
                                    </Box>
                                </Box>

                                {/* Unlike a label in the box or one from the return portal, which are
                                    invoiced once used, this label is invoiced the moment it is made. */}
                                <Box>
                                    <Text light>{t("return_label_invoiced_on_creation")}</Text>
                                </Box>
                            </>
                        )}
                        </>
                        }

                        {showDetails &&
                            <>
                                {TextField({ label: t("name"), name: "name", value: form.name, error: fieldErrors.name, width: 160 })}
                                {TextField({ label: t("reference"), name: "reference", value: form.reference, error: fieldErrors.reference, width: 160 })}

                                {/* "Onverkoopbaar verklaren": nothing on this return goes back
                                    into stock, whatever the desk finds in the parcel. */}
                                <Box sx={rowSx}>
                                    <Box sx={{ width: 160 }}>
                                        <Text>{t("unsellable")}</Text>
                                    </Box>
                                    <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                                        <ShSwitch
                                            checked={form.rejectAll}
                                            onChange={(e) => handleChange("rejectAll", e.target.checked)}
                                        />
                                    </Box>
                                </Box>

                                {/* Sender: the party posting the parcel back, needed only to make a
                                    label. The return destination is derived server-side from the
                                    warehouse + relation and cannot be set here. */}
                                {form.createLabel &&
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Text bold>{t("sender")}</Text>
                                    {relationAddressOptions.length > 0 &&
                                        <Box sx={rowSx}>
                                            <Box sx={{ width: 120 }}>
                                                <Text>{t("standard_address")}</Text>
                                            </Box>
                                            <Box sx={inputBoxSx}>
                                                <ShValidatedInputDropdown
                                                    name="relationAddress"
                                                    displayName="name"
                                                    changeField="id"
                                                    options={relationAddressOptions}
                                                    noSelection={t("choose_option")}
                                                    onChange={handleRelationAddressChange}
                                                    selected={selectedAddressIndex}
                                                    closeOnSelection
                                                    searchOptions={{
                                                        enabled: true,
                                                        placeholder: t("search")
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    }
                                    {renderSenderFields(form.sender)}
                                </Box>
                                }

                                {/* Expected products. The product, the amount and why it is coming
                                    back are set here; the warehouse decides per line what is accepted. */}
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <Text bold>{t("products")}</Text>
                                        <Shbutton
                                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                            variant="contained"
                                            onClick={addReturnLine}
                                        >
                                            {t("add")}
                                        </Shbutton>
                                    </Box>

                                    {fieldErrors.returnLines &&
                                        <Text variant="red">{fieldErrors.returnLines}</Text>
                                    }

                                    {form.returnLines.map((line, index) => (
                                        <Box
                                            key={"return-line-" + index}
                                            sx={{
                                                width: 1,
                                                borderRadius: 4,
                                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                p: 2,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Box sx={{ ...inputBoxSx, flex: 3 }}>
                                                    <ShValidatedInputDropdown
                                                        height={32}
                                                        displayName="name"
                                                        changeField="id"
                                                        valueKey="id"
                                                        closeOnSelection
                                                        searchOptions={{
                                                            enabled: true,
                                                            placeholder: t("search_product_placeholder"),
                                                            searchables: ["barcodes", "name"]
                                                        }}
                                                        name={`${index}.productId`}
                                                        selected={productOptions?.find(p => p.id === line.productId)?.id ?? -1}
                                                        options={productOptions}
                                                        noSelection={t("choose_option")}
                                                        onChange={handleLineProductChange}
                                                    />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                                    <NumberInput
                                                        value={line.amount}
                                                        change={(value) => handleLineAmountChange(index, value)}
                                                    />
                                                </Box>
                                                <ButtonBase
                                                    tabIndex={-1}
                                                    onClick={() => handleDeleteLine(index)}
                                                    sx={{
                                                        height: 32,
                                                        width: 32,
                                                        borderRadius: 5,
                                                        color: colors.red[400],
                                                        background: colors.red[100],
                                                    }}
                                                >
                                                    <DeleteOutlinedIcon fontSize='small' />
                                                </ButtonBase>
                                            </Box>

                                            {/* Why this product is coming back. A relation with no
                                                reasons configured gets no selector at all. */}
                                            {reasonOptions.length > 0 &&
                                                <Box sx={rowSx}>
                                                    <Box sx={{ width: 120 }}>
                                                        <Text light>{t("reason")}</Text>
                                                    </Box>
                                                    <Box sx={inputBoxSx}>
                                                        <ShValidatedInputDropdown
                                                            height={32}
                                                            displayName="name"
                                                            changeField="id"
                                                            valueKey="id"
                                                            closeOnSelection
                                                            name={`${index}.returnReasonId`}
                                                            selected={reasonOptions?.find(r => r.id === line.returnReasonId)?.id ?? -1}
                                                            options={reasonOptions}
                                                            noSelection={t("choose_option")}
                                                            onChange={handleLineReasonChange}
                                                        />
                                                    </Box>
                                                </Box>
                                            }
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        }
                    </>
                )}
            </Box>

            {/* Kept out of the scrolling area above: a carrier's refusal is the reason the
                return failed, so it sits in view next to the buttons instead of at the
                bottom of a list the user has to scroll back through. */}
            {shippingErrors.length > 0 &&
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        paddingX: 3,
                        paddingTop: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 1,
                            background: colors.red[100],
                            borderRadius: 4,
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            maxHeight: "20vh",
                            overflowY: "auto",
                        }}
                    >
                        {shippingErrors.map((err, idx) => (
                            <Text key={"se" + idx} bold variant={"red"}>{err}</Text>
                        ))}
                        <ShPayloadToggle payload={shippingPayload} />
                    </Box>
                </Box>
            }

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    justifyContent: "space-between",
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>
                    {result ? t("close") : t("cancel")}
                </Shbutton>
                {!result &&
                    <ButtonGroup version={2}>
                        <Shbutton isLoading={isLoading} disabled={submitDisabled} className={"group-btn"} color={"green"}
                            variant="contained"
                            onClick={handleSubmit}
                        >{t("create")}</Shbutton>
                    </ButtonGroup>
                }
            </Box>

            <MSelectLabel
                open={labelsModal.open}
                handleClose={() => setLabelsModal({ open: false, labels: [], type: null })}
                labels={labelsModal.labels}
                type={labelsModal.type}
            />
        </SHModal>
    );
};

export default MCreateReturn;
